/**
 * 纯前端模拟账号登录/注册（localStorage）。
 * 默认不走此逻辑；使用 `yarn start:mock` 或 REACT_APP_USE_FRONTEND_MOCK_AUTH=true 时启用。
 */

const STORAGE_USERS = 'masspayment_mock_account_users';
const STORAGE_SESSION = 'masspayment_mock_account_session';

type StoredUser = {
  username: string;
  email: string;
  /** 仅本地模拟存储，勿用于真实安全场景 */
  password: string;
};

type MockSession = {
  token: string;
  username: string;
  email: string;
};

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

function readUsers(): StoredUser[] {
  try {
    const raw = localStorage.getItem(STORAGE_USERS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredUser[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeUsers(users: StoredUser[]) {
  localStorage.setItem(STORAGE_USERS, JSON.stringify(users));
}

/** 与 mock/user.ts 中常用测试账号对齐，便于从 Umi mock 迁移 */
function seedIfEmpty(users: StoredUser[]): StoredUser[] {
  if (users.length > 0) return users;
  const seeded: StoredUser[] = [
    { username: '1', email: '1@mock.local', password: '1' },
    { username: 'test001', email: 'test001@mock.local', password: '123456' },
    { username: 'admin', email: 'admin@mock.local', password: 'ant.design' },
    { username: 'user', email: 'user@mock.local', password: 'ant.design' },
  ];
  writeUsers(seeded);
  return seeded;
}

export function getMockAccountSession(): MockSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_SESSION);
    if (!raw) return null;
    const s = JSON.parse(raw) as MockSession;
    if (s?.token && s?.username) return s;
    return null;
  } catch {
    return null;
  }
}

export function clearMockAccountSession() {
  localStorage.removeItem(STORAGE_SESSION);
}

function setMockSession(session: MockSession) {
  localStorage.setItem(STORAGE_SESSION, JSON.stringify(session));
}

function randomToken() {
  return `mock_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

export async function frontendMockLogin(params: API.LoginParams): Promise<API.LoginResult> {
  await delay(400);
  const username = (params.username || '').trim();
  const password = params.password || '';
  if (!username || !password) {
    return { status: 'error', type: params.type, currentAuthority: 'guest' };
  }
  const users = seedIfEmpty(readUsers());
  const found = users.find((u) => u.username === username && u.password === password);
  if (!found) {
    return { status: 'error', type: params.type, currentAuthority: 'guest' };
  }
  const session: MockSession = {
    token: randomToken(),
    username: found.username,
    email: found.email,
  };
  setMockSession(session);
  return { status: 'ok', type: params.type, currentAuthority: 'admin' };
}

export async function frontendMockRegister(body: API.RegisterParams): Promise<API.RegisterResult> {
  await delay(400);
  const username = (body.username || '').trim();
  const email = (body.email || '').trim();
  const password = body.password || '';
  if (!username || !email || !password) {
    return { status: 'error', success: false };
  }
  let users = seedIfEmpty(readUsers());
  if (users.some((u) => u.username.toLowerCase() === username.toLowerCase())) {
    return { status: 'error', success: false };
  }
  if (users.some((u) => u.email.toLowerCase() === email.toLowerCase())) {
    return { status: 'error', success: false };
  }
  users = [...users, { username, email, password }];
  writeUsers(users);
  return { status: 'ok', success: true, currentAuthority: 'user' };
}

/** 构造与 mock/user 及业务分支兼容的 CurrentUser（无商户信息时走 app.tsx 中 merchant 缺失分支） */
export function buildCurrentUserFromMockSession(session: MockSession): API.CurrentUser {
  return {
    name: session.username,
    userid: session.username,
    email: session.email,
    avatar: 'https://gw.alipayobjects.com/zos/antfincdn/XAosXuNZyF/BiazfanxmamNRoxxVxka.png',
    signature: '',
    title: '',
    group: '',
    tags: [],
    notifyCount: 0,
    unreadCount: 0,
    country: 'China',
    access: 'admin',
    geographic: {
      province: { label: '', key: '' },
      city: { label: '', key: '' },
    },
    address: '',
    phone: '',
    roles: ['partner', 'partnerDirectCustomer'],
    merchant: {} as API.CurrentUser['merchant'],
  };
}
